'use client';

import React from 'react';
import Image from 'next/image';
import { MessageCircle, Repeat2, Heart, Archive, ExternalLink } from 'lucide-react';

interface CachedTweet {
  id: string;
  text: string;
  username: string;
  displayName?: string;
  authorProfilePic?: string | null;
  createdAt?: string | null;
  media?: Array<{
    type: 'photo' | 'video' | 'gif';
    url: string;
    thumbnailUrl?: string;
  }>;
  metrics?: {
    likes: number;
    retweets: number;
    replies: number;
  };
  tweetUrl: string;
  isRetweet?: boolean;
  retweetedTweet?: {
    username: string;
    displayName?: string;
    text: string;
    authorProfilePic?: string;
    media?: Array<{ type: string; url: string }>;
  } | null;
  quotedTweet?: {
    username: string;
    text: string;
    authorProfilePic?: string;
    media?: Array<{ type: string; url: string }>;
  } | null;
}

interface ArchivedTweetCardProps {
  tweet: CachedTweet;
  // 'archived' = tweet is genuinely gone, show the amber "may be unavailable" banner.
  // 'live'     = render path crashed but the tweet still works — use cache as a
  //              graceful substitute without claiming it's stale.
  variant?: 'archived' | 'live';
}

export function ArchivedTweetCard({ tweet, variant = 'archived' }: ArchivedTweetCardProps) {
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatNumber = (num?: number) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const cleanTweetText = (text: string) => {
    // Remove t.co links that appear at the end of the tweet
    return text.replace(/\s*https:\/\/t\.co\/\w+\s*$/g, '').trim();
  };

  // Determine what content to show
  const displayContent = tweet.isRetweet && tweet.retweetedTweet
    ? tweet.retweetedTweet
    : tweet;

  return (
    <div className="my-6 flex justify-center">
      <div
        className="w-full bg-white dark:bg-brand-dark-lighter rounded-xl border border-gray-200 dark:border-neutral-800 overflow-hidden"
        style={{ maxWidth: '550px' }}
      >
        {/* Archived Banner — only shown when the tweet is genuinely gone */}
        {variant === 'archived' && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/50 px-4 py-2 flex items-center gap-2">
            <Archive className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
              Archived Tweet — Original may be unavailable
            </span>
          </div>
        )}

        <div className="p-4">
          {/* Retweet Indicator */}
          {tweet.isRetweet && tweet.retweetedTweet && (
            <div className="flex items-center gap-1.5 mb-3 text-xs text-gray-500 dark:text-gray-400">
              <Repeat2 className="w-3.5 h-3.5" />
              <span>@{tweet.username} retweeted</span>
            </div>
          )}

          {/* Header */}
          <div className="flex items-start gap-3 mb-3">
            {/* Profile Image */}
            <div className="relative w-10 h-10 flex-shrink-0">
              {(tweet.isRetweet ? tweet.retweetedTweet?.authorProfilePic : tweet.authorProfilePic) ? (
                <Image
                  src={(tweet.isRetweet ? tweet.retweetedTweet?.authorProfilePic : tweet.authorProfilePic)!}
                  alt={displayContent.username || 'User'}
                  fill
                  className="rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center text-white font-bold">
                  {(displayContent.username || 'U').charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Name & Handle */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-gray-900 dark:text-white">
                  {displayContent.displayName || displayContent.username}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  @{displayContent.username}
                </span>
              </div>
              {tweet.createdAt && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(tweet.createdAt)}
                </span>
              )}
            </div>
          </div>

          {/* Tweet Text */}
          <div className="text-gray-900 dark:text-gray-100 text-[15px] leading-relaxed whitespace-pre-wrap mb-3">
            {cleanTweetText(
              tweet.isRetweet && tweet.retweetedTweet
                ? tweet.retweetedTweet.text
                : tweet.text
            )}
          </div>

          {/* Quoted Tweet */}
          {tweet.quotedTweet && (
            <div className="border border-gray-200 dark:border-neutral-700 rounded-xl p-3 mb-3">
              <div className="flex items-center gap-2 mb-2">
                {tweet.quotedTweet.authorProfilePic && (
                  <div className="relative w-5 h-5">
                    <Image
                      src={tweet.quotedTweet.authorProfilePic}
                      alt={tweet.quotedTweet.username}
                      fill
                      className="rounded-full object-cover"
                    />
                  </div>
                )}
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  @{tweet.quotedTweet.username}
                </span>
              </div>
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {cleanTweetText(tweet.quotedTweet.text)}
              </div>
            </div>
          )}

          {/* Media */}
          {tweet.media && tweet.media.length > 0 && tweet.media[0].url && (
            <div className="rounded-xl overflow-hidden mb-3 bg-gray-100 dark:bg-neutral-800">
              {tweet.media[0].type === 'photo' ? (
                <img
                  src={tweet.media[0].url}
                  alt="Tweet media"
                  className="w-full h-auto object-contain max-h-[400px]"
                  loading="lazy"
                />
              ) : tweet.media[0].type === 'gif' ? (
                <div className="relative">
                  <video
                    src={tweet.media[0].url}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto object-contain max-h-[400px]"
                  />
                  <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                    GIF
                  </div>
                </div>
              ) : (
                <div className="relative aspect-video bg-black flex items-center justify-center">
                  {tweet.media[0].thumbnailUrl && (
                    <Image
                      src={tweet.media[0].thumbnailUrl}
                      alt="Video thumbnail"
                      fill
                      className="object-cover opacity-60"
                    />
                  )}
                  <span className="relative z-10 text-white text-sm">Video archived</span>
                </div>
              )}
              {tweet.media.length > 1 && (
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                  +{tweet.media.length - 1}
                </div>
              )}
            </div>
          )}

          {/* Retweeted Tweet Media */}
          {tweet.isRetweet && tweet.retweetedTweet?.media && tweet.retweetedTweet.media.length > 0 && (
            <div className="rounded-xl overflow-hidden mb-3 bg-gray-100 dark:bg-neutral-800">
              <img
                src={tweet.retweetedTweet.media[0].url}
                alt="Retweeted media"
                className="w-full h-auto object-contain max-h-[400px]"
                loading="lazy"
              />
            </div>
          )}

          {/* Footer - Metrics */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-neutral-800">
            <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400 text-sm">
              <div className="flex items-center gap-1.5">
                <MessageCircle className="w-4 h-4" />
                <span>{formatNumber(tweet.metrics?.replies)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Repeat2 className="w-4 h-4" />
                <span>{formatNumber(tweet.metrics?.retweets)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Heart className="w-4 h-4" />
                <span>{formatNumber(tweet.metrics?.likes)}</span>
              </div>
            </div>

            {/* View on X (may not work if deleted) */}
            <a
              href={tweet.tweetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Try viewing on X (may be unavailable)"
            >
              <span>View on X</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        {/* Eureka Attribution */}
        <div className="bg-gray-50 dark:bg-neutral-900/50 border-t border-gray-100 dark:border-neutral-800 px-4 py-2">
          <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center">
            Preserved by Descout • Cached at time of publication
          </p>
        </div>
      </div>
    </div>
  );
}

export default ArchivedTweetCard;
